import { useEffect, useRef, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { StatusPill } from '../../components/StatusPills';
import Sparkline from '../../components/Sparkline';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Loader2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ConfirmDialog';

const POLL_MS = 8000;

const STATUS_TONE = { pending: 'needs', approved: 'done', revoked: 'neutral' };
const STATUS_LABEL = { pending: 'Pending approval', approved: 'Approved', revoked: 'Revoked' };

const formatWhen = (iso) => {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const round = (n, d = 0) => (typeof n === 'number' ? n.toFixed(d) : '—');

/** The one-time enrolment code dialog — shown right after "Add a worker". */
const EnrollDialog = ({ open, onOpenChange, code, expiresAt }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add a worker</DialogTitle>
        <DialogDescription>
          Run this on the new worker box. It generates a certificate and key locally
          (the key never leaves that box), registers with this enrolment code, and
          writes a ready-to-use env file.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <p className="text-xs text-muted-foreground mb-1">On the worker box:</p>
          <pre className="rounded-md bg-inset p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
            ./generate-worker-client-cert.sh --register {window.location.origin} {code}
          </pre>
        </div>
        <p className="text-xs text-muted-foreground">
          This code expires {expiresAt ? new Date(expiresAt).toLocaleTimeString() : 'soon'} and can only be used once.
          The worker will show up below as "Pending approval" — compare the fingerprint it
          prints against what's shown here before approving.
        </p>
      </div>
      <DialogFooter>
        <Button onClick={() => onOpenChange(false)}>Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const shortSha = (sha) => (sha ? sha.slice(0, 7) : null);

const WorkerRow = ({ worker, onApprove, onRevoke, onDelete, onRename, onUpdate, onUpdateConcurrency, busy, apiGitSha }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(worker.label);
  const [editingConcurrency, setEditingConcurrency] = useState(false);
  const [concurrencyValue, setConcurrencyValue] = useState(worker.concurrency ?? '');
  const stats = worker.stats || {};
  const history = worker.stats_history || [];
  const workerSha = stats.git_sha || null;
  const outOfDate = worker.status === 'approved' && apiGitSha && workerSha && workerSha !== apiGitSha;

  const save = async () => {
    if (label.trim() && label.trim() !== worker.label) await onRename(worker.id, label.trim());
    setEditing(false);
  };

  const saveConcurrency = async () => {
    const trimmed = String(concurrencyValue).trim();
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 1)) {
      setConcurrencyValue(worker.concurrency ?? '');
      setEditingConcurrency(false);
      return;
    }
    if (parsed !== (worker.concurrency ?? null)) await onUpdateConcurrency(worker.id, parsed);
    setEditingConcurrency(false);
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid={`worker-row-${worker.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-7 w-48" autoFocus />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={save}><Check className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setLabel(worker.label); setEditing(false); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </>
            ) : (
              <>
                <p className="font-medium text-foreground">{worker.label}</p>
                <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground" aria-label="Rename">
                  <Pencil className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
          <p className="text-xs font-mono text-muted-foreground mt-1">{worker.id}</p>
          <p className="text-xs font-mono text-muted-foreground break-all mt-1" data-testid={`worker-fingerprint-${worker.id}`}>
            {worker.fingerprint}
          </p>
          {worker.status === 'approved' && (
            <p className="text-xs text-muted-foreground mt-1" data-testid={`worker-version-${worker.id}`}>
              Version:{' '}
              <span className="font-mono">{shortSha(workerSha) || 'unknown'}</span>
              {outOfDate && <span className="text-amber-600 dark:text-amber-500"> · out of date</span>}
              {worker.update_requested && <span className="text-muted-foreground"> · update requested…</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusPill tone={STATUS_TONE[worker.status]} size="sm">{STATUS_LABEL[worker.status]}</StatusPill>
          {worker.status === 'pending' && (
            <Button size="sm" onClick={() => onApprove(worker.id)} disabled={busy}>
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Approve
            </Button>
          )}
          {worker.status === 'approved' && (
            <Button
              size="sm"
              variant={outOfDate ? 'default' : 'outline'}
              onClick={() => onUpdate(worker.id)}
              disabled={busy || worker.update_requested}
              data-testid={`worker-update-${worker.id}`}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {worker.update_requested ? 'Update requested' : 'Update'}
            </Button>
          )}
          {worker.status === 'approved' && (
            <Button size="sm" variant="outline" onClick={() => onRevoke(worker.id)} disabled={busy}>Revoke</Button>
          )}
          {worker.status !== 'approved' && (
            <Button size="sm" variant="ghost" onClick={() => onDelete(worker.id)} disabled={busy}>Delete</Button>
          )}
        </div>
      </div>

      {worker.status === 'approved' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground">CPU</p>
            <p className="text-sm font-mono text-foreground">{round(stats.cpu_percent)}%</p>
            <Sparkline values={(history || []).map((h) => h.cpu_percent)} className="text-muted-foreground mt-1" max={100} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Memory</p>
            <p className="text-sm font-mono text-foreground">{round(stats.mem_percent)}%</p>
            <Sparkline values={(history || []).map((h) => h.mem_percent)} className="text-muted-foreground mt-1" max={100} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Load (1m)</p>
            <p className="text-sm font-mono text-foreground">{round(stats.load1, 2)}</p>
            <Sparkline values={(history || []).map((h) => h.load1)} className="text-muted-foreground mt-1" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Active jobs</p>
            <p className="text-sm font-mono text-foreground">{stats.active_jobs ?? '—'}</p>
            <p className="text-xs text-muted-foreground mt-1">Last seen {formatWhen(worker.last_seen_at)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Concurrency</p>
            {editingConcurrency ? (
              <div className="flex items-center gap-1 mt-0.5">
                <Input
                  type="number" min="1" max="20" autoFocus
                  value={concurrencyValue}
                  onChange={(e) => setConcurrencyValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveConcurrency()}
                  className="h-7 w-16 text-sm font-mono"
                  data-testid={`worker-concurrency-input-${worker.id}`}
                />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={saveConcurrency}>
                  <Check className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => { setConcurrencyValue(worker.concurrency ?? ''); setEditingConcurrency(false); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-mono text-foreground" data-testid={`worker-concurrency-${worker.id}`}>
                  {worker.concurrency ?? 'default'}
                </p>
                <button onClick={() => setEditingConcurrency(true)} className="text-muted-foreground hover:text-foreground" aria-label="Edit concurrency">
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Documents worked on at once</p>
          </div>
        </div>
      )}
    </div>
  );
};

const SelfStatsCard = ({ stats }) => {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="font-medium text-foreground">This server</p>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">The API process itself, right now.</p>
          </div>
          {stats?.git_sha && (
            <p className="text-xs font-mono text-muted-foreground mb-3">Version: {shortSha(stats.git_sha)}</p>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">CPU</p>
            <p className="text-sm font-mono text-foreground">{round(stats?.cpu_percent)}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Memory</p>
            <p className="text-sm font-mono text-foreground">
              {round(stats?.mem_percent)}%{stats?.mem_used_mb ? ` (${Math.round(stats.mem_used_mb)}MB)` : ''}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Load (1m/5m/15m)</p>
            <p className="text-sm font-mono text-foreground">
              {round(stats?.load1, 2)} / {round(stats?.load5, 2)} / {round(stats?.load15, 2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Disk</p>
            <p className="text-sm font-mono text-foreground">{round(stats?.disk_percent)}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminInfrastructureWorkers = () => {
  const [workers, setWorkers] = useState(null);
  const [selfStats, setSelfStats] = useState(null);
  const [busy, setBusy] = useState({});
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollCode, setEnrollCode] = useState(null);
  const [creatingCode, setCreatingCode] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [workersRes, selfRes] = await Promise.all([
        adminApi.listAnalysisWorkers(),
        adminApi.apiSelfStats().catch(() => null),
      ]);
      setWorkers(workersRes.data?.workers || []);
      if (selfRes) setSelfStats(selfRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load workers');
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  const withBusy = async (id, fn) => {
    setBusy((prev) => ({ ...prev, [id]: true }));
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That action failed');
    } finally {
      setBusy((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleAdd = async () => {
    setCreatingCode(true);
    try {
      const res = await adminApi.createWorkerEnrollCode();
      setEnrollCode(res.data);
      setEnrollOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create an enrolment code');
    } finally {
      setCreatingCode(false);
    }
  };

  const handleApprove = (id) => withBusy(id, async () => {
    await adminApi.approveAnalysisWorker(id);
    toast.success('Worker approved');
  });
  const confirmDialog = useConfirm();
  const handleRevoke = async (id) => {
    if (!(await confirmDialog({ title: 'Revoke this worker?', description: 'It immediately stops being able to fetch any keys.', confirmLabel: 'Revoke', destructive: true }))) return;
    withBusy(id, async () => {
      await adminApi.revokeAnalysisWorker(id);
      toast.success('Worker revoked');
    });
  };
  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Delete this worker entry?', description: 'This cannot be undone.', confirmLabel: 'Delete', destructive: true }))) return;
    withBusy(id, async () => {
      await adminApi.deleteAnalysisWorker(id);
      toast.success('Worker deleted');
    });
  };
  const handleRename = (id, newLabel) => withBusy(id, () => adminApi.renameAnalysisWorker(id, newLabel));
  const handleUpdate = (id) => withBusy(id, async () => {
    await adminApi.requestAnalysisWorkerUpdate(id);
    toast.success('Update requested — it will pull the latest code and restart at its next heartbeat');
  });
  const handleUpdateConcurrency = (id, concurrency) => withBusy(id, async () => {
    await adminApi.updateAnalysisWorkerSettings(id, { concurrency });
    toast.success(concurrency ? `Concurrency set to ${concurrency}` : 'Concurrency reset to default');
  });

  const rows = workers || [];
  const pending = rows.filter((w) => w.status === 'pending');
  const approved = rows.filter((w) => w.status === 'approved');
  const revoked = rows.filter((w) => w.status === 'revoked');

  return (
    <div className="space-y-6" data-testid="admin-infrastructure-workers">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground">Analysis workers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Servers that run document analysis. Any number can run at once — add one whenever
            you need more capacity.
          </p>
        </div>
        <Button onClick={handleAdd} disabled={creatingCode} data-testid="add-worker-button">
          {creatingCode ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Add a worker
        </Button>
      </div>

      <SelfStatsCard stats={selfStats} />

      {workers === null ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </p>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-5 text-sm text-muted-foreground">
          No standalone analysis workers yet. Analysis is running in-process on the API server itself.
        </CardContent></Card>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <div className="space-y-3">
              <p className="pd-eyebrow">Pending approval ({pending.length})</p>
              {pending.map((w) => (
                <WorkerRow key={w.id} worker={w} busy={!!busy[w.id]} apiGitSha={selfStats?.git_sha}
                  onApprove={handleApprove} onRevoke={handleRevoke} onDelete={handleDelete} onRename={handleRename} onUpdate={handleUpdate} onUpdateConcurrency={handleUpdateConcurrency} />
              ))}
            </div>
          )}
          {approved.length > 0 && (
            <div className="space-y-3">
              <p className="pd-eyebrow">Approved ({approved.length})</p>
              {approved.map((w) => (
                <WorkerRow key={w.id} worker={w} busy={!!busy[w.id]} apiGitSha={selfStats?.git_sha}
                  onApprove={handleApprove} onRevoke={handleRevoke} onDelete={handleDelete} onRename={handleRename} onUpdate={handleUpdate} onUpdateConcurrency={handleUpdateConcurrency} />
              ))}
            </div>
          )}
          {revoked.length > 0 && (
            <div className="space-y-3">
              <p className="pd-eyebrow">Revoked ({revoked.length})</p>
              {revoked.map((w) => (
                <WorkerRow key={w.id} worker={w} busy={!!busy[w.id]} apiGitSha={selfStats?.git_sha}
                  onApprove={handleApprove} onRevoke={handleRevoke} onDelete={handleDelete} onRename={handleRename} onUpdate={handleUpdate} onUpdateConcurrency={handleUpdateConcurrency} />
              ))}
            </div>
          )}
        </div>
      )}

      <EnrollDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        code={enrollCode?.code}
        expiresAt={enrollCode?.expires_at}
      />
    </div>
  );
};

export default AdminInfrastructureWorkers;
